/**
 * Stays Lost & Found shared data model, pure validators, categories,
 * status transitions, and retention rules.
 *
 * Spec: docs/plans/sector-operations.md row K7 (SO-P115, SO-D207, SO-D294, SO-D295, SO-D296, SO-D297).
 */

import { isE164Phone, maskPhone } from './phone.js'

export const DEFAULT_HOLD_DAYS = 90
export const MIN_HOLD_DAYS = 30
export const MAX_HOLD_DAYS = 365

export const DEFAULT_VALUABLE_HOLD_DAYS = 180
export const MIN_VALUABLE_HOLD_DAYS = 30
export const MAX_VALUABLE_HOLD_DAYS = 730

export const LF_CATEGORIES = [
  'CLOTHING',
  'ELECTRONICS',
  'DOCUMENTS',
  'JEWELLERY',
  'MONEY',
  'KEYS',
  'BAGS',
  'OTHER',
] as const

export type LostFoundCategory = (typeof LF_CATEGORIES)[number]

export const VALUABLE_CATEGORIES: readonly LostFoundCategory[] = [
  'JEWELLERY',
  'MONEY',
  'ELECTRONICS',
  'DOCUMENTS',
] as const

export const LF_ITEM_STATUSES = [
  'LOGGED',
  'CLAIM_OPEN',
  'RETURNED',
  'DISPOSAL_PROPOSED',
  'DISPOSED',
] as const

export type LostFoundItemStatus = (typeof LF_ITEM_STATUSES)[number]

export const LF_CLAIM_STATUSES = [
  'OPEN',
  'DESCRIBED',
  'MATCHED',
  'CLOSED_RETURNED',
  'CLOSED_REJECTED',
  'CLOSED_CANCELLED',
  'CLOSED_EXPIRED',
] as const

export type LostFoundClaimStatus = (typeof LF_CLAIM_STATUSES)[number]

export const DISPOSAL_METHODS = [
  'DONATE',
  'DISCARD',
  'GIVE_TO_FINDER',
  'HAND_TO_POLICE',
] as const

export type LostFoundDisposalMethod = (typeof DISPOSAL_METHODS)[number]

export const LF_RETURN_MODES = ['PICKUP', 'COURIER'] as const
export type LostFoundReturnMode = (typeof LF_RETURN_MODES)[number]

export const LF_DESK_VERDICTS = ['MATCH', 'NO_MATCH'] as const
export type LostFoundDeskVerdict = (typeof LF_DESK_VERDICTS)[number]

export const LF_EVENT_TYPES = [
  'LOGGED',
  'BIN_MOVED',
  'PHOTO_ADDED',
  'CLAIM_STARTED',
  'CLAIM_LINK_REISSUED',
  'CLAIM_DESCRIBED',
  'CLAIM_MATCHED',
  'CLAIM_REJECTED',
  'CLAIM_CANCELLED',
  'CLAIM_EXPIRED',
  'HANDED_OVER',
  'COURIERED',
  'DISPOSAL_PROPOSED',
  'DISPOSAL_SIGNED',
  'DISPOSAL_WITHDRAWN',
  'DISPOSED',
  'CLAIMANT_DATA_PURGED',
  'CLAIMANT_SIGN_IN_REMOVED',
  'CLAIMANT_SIGN_IN_KEPT',
] as const

export type LostFoundEventType = (typeof LF_EVENT_TYPES)[number]

export const MIN_ITEM_DESCRIPTION_LENGTH = 3
export const MAX_ITEM_DESCRIPTION_LENGTH = 500
export const MAX_ITEM_PHOTOS = 4
export const MAX_BIN_LABEL_LENGTH = 40

export const MIN_CLAIMANT_NAME_LENGTH = 1
export const MAX_CLAIMANT_NAME_LENGTH = 80

export const MIN_GUEST_DESCRIPTION_LENGTH = 3
export const MAX_GUEST_DESCRIPTION_LENGTH = 1000
export const MAX_COURIER_TRACKING_REF_LENGTH = 60

export const CLAIM_LINK_EXPIRY_DAYS = 7
export const CLAIM_PURGE_DAYS = 30
export const CLAIM_LINK_EXPIRY_MS = CLAIM_LINK_EXPIRY_DAYS * 24 * 60 * 60 * 1000
export const CLAIM_PURGE_MS = CLAIM_PURGE_DAYS * 24 * 60 * 60 * 1000

export const MIN_DISPOSAL_NOTE_LENGTH = 3
export const MAX_DISPOSAL_NOTE_LENGTH = 500

export const MIN_CANCEL_REASON_LENGTH = 3
export const MAX_CANCEL_REASON_LENGTH = 500

export const MAX_COURIER_ADDRESS_LINES = 3
export const MAX_COURIER_ADDRESS_LINE_LENGTH = 200
export const PIN_CODE_REGEX = /^\d{6}$/

// --- Data Types ---

export interface LostFoundSettingsDoc {
  workspaceId: string
  propertyId: string
  holdDays: number
  valuableHoldDays: number
  version: number
  createdAt?: number
  updatedAt?: number
  updatedBy?: string
}

export interface LostFoundItemDisposal {
  method: LostFoundDisposalMethod
  proposedBy: string
  proposedAt: number
  proposalNote: string
  signedBy?: string
  signedAt?: number
}

export interface LostFoundItemDoc {
  id: string
  workspaceId: string
  sector: 'stays'
  propertyId: string
  placeId: string
  foundAt: number
  foundBy: string
  description: string
  category: LostFoundCategory
  valuable: boolean
  photos: string[]
  binLabel: string
  status: LostFoundItemStatus
  holdUntil: number
  activeClaimId?: string | null
  disposal?: LostFoundItemDisposal | null
  readAccess?: string[] | null
  version: number
  createdAt: number
  updatedAt: number
}

export interface LostFoundEventDoc {
  id: string
  itemId: string
  workspaceId: string
  type: LostFoundEventType
  byUid: string
  at: number
  note?: string
}

export interface LostFoundClaimDoc {
  id: string
  workspaceId: string
  itemId: string
  propertyId: string
  status: LostFoundClaimStatus
  phoneMasked: string
  returnMode?: LostFoundReturnMode
  guestDescription?: string
  deskVerdict?: LostFoundDeskVerdict
  courierTrackingRef?: string
  linkExpiresAt: number
  closedAt?: number
  purgeAfter?: number
  purgedAt?: number
  createdBy: string
  readAccess?: string[] | null
  version: number
  createdAt: number
  updatedAt: number
}

export interface LostFoundCourierAddress {
  lines: string[]
  city: string
  pin: string
  state: string
}

export interface LostFoundClaimPiiDoc {
  claimId: string
  workspaceId: string
  itemId: string
  claimantName: string
  phoneE164: string
  courierAddress?: LostFoundCourierAddress
  createdAt: number
  updatedAt: number
}

export interface LostFoundClaimSecretsDoc {
  claimId: string
  tokenHash: string
  phoneHmac: string
  boundUid?: string
  otpPrepared: number[]
  createdAt: number
  updatedAt: number
}

// --- Callable Input Types ---

export interface LogFoundItemInput {
  workspaceId: string
  propertyId: string
  placeId: string
  description: string
  category: LostFoundCategory
  valuable?: boolean
  binLabel?: string
  foundAt?: number
  opId: string
}

export interface RecordFoundItemPhotoInput {
  itemId: string
  path: string
  opId: string
}

export interface MoveFoundItemBinInput {
  itemId: string
  binLabel: string
  opId: string
}

export interface StartLostFoundClaimInput {
  itemId: string
  claimantName: string
  phoneE164: string
  opId: string
}

export interface ReissueLostFoundClaimLinkInput {
  claimId: string
  opId: string
}

export interface CancelLostFoundClaimInput {
  claimId: string
  reason: string
  opId: string
}

export interface VerifyLostFoundClaimInput {
  claimId: string
  verdict: LostFoundDeskVerdict
  note?: string
  opId: string
}

export interface CompleteLostFoundReturnInput {
  claimId: string
  mode: LostFoundReturnMode
  courierTrackingRef?: string
  opId: string
}

export interface ProposeLostFoundDisposalInput {
  itemId: string
  method: LostFoundDisposalMethod
  note: string
  opId: string
}

export interface SignLostFoundDisposalInput {
  itemId: string
  opId: string
}

export interface WithdrawLostFoundDisposalInput {
  itemId: string
  reason: string
  opId: string
}

export interface SetLostFoundSettingsInput {
  workspaceId: string
  propertyId: string
  holdDays: number
  valuableHoldDays: number
  expectedVersion?: number
  opId: string
}

export interface GetLostFoundClaimantInput {
  claimId: string
}

export interface GetLostClaimLinkInfoInput {
  token: string
}

export interface PrepareLostClaimOtpInput {
  token: string
}

export interface OpenLostClaimInput {
  token: string
}

export interface DescribeLostClaimInput {
  token: string
  guestDescription: string
  returnMode: LostFoundReturnMode
  courierAddress?: LostFoundCourierAddress
  opId: string
}

// --- Pure Helper Functions ---

export function buildLostFoundSettingsKey(workspaceId: string, propertyId: string): string {
  return `${workspaceId}_${propertyId}`
}

export function isValuableCategory(category: LostFoundCategory): boolean {
  return VALUABLE_CATEGORIES.includes(category)
}

export function computeHoldUntil(
  foundAt: number,
  category: LostFoundCategory,
  valuableOverride?: boolean,
  settings?: { holdDays?: number; valuableHoldDays?: number },
): { holdUntil: number; valuable: boolean } {
  const valuable = typeof valuableOverride === 'boolean' ? valuableOverride : isValuableCategory(category)
  const holdDays = settings?.holdDays ?? DEFAULT_HOLD_DAYS
  const valuableHoldDays = settings?.valuableHoldDays ?? DEFAULT_VALUABLE_HOLD_DAYS
  const days = valuable ? valuableHoldDays : holdDays
  const holdUntil = foundAt + days * 24 * 60 * 60 * 1000
  return { holdUntil, valuable }
}

export function validateLostFoundSettings(input: unknown): {
  holdDays: number
  valuableHoldDays: number
} {
  if (typeof input !== 'object' || input === null) {
    throw new Error('Settings input must be an object.')
  }
  const raw = input as Record<string, unknown>
  const holdDays = Number(raw.holdDays)
  if (!Number.isInteger(holdDays) || holdDays < MIN_HOLD_DAYS || holdDays > MAX_HOLD_DAYS) {
    throw new Error(`holdDays must be an integer between ${MIN_HOLD_DAYS} and ${MAX_HOLD_DAYS}.`)
  }
  const valuableHoldDays = Number(raw.valuableHoldDays)
  if (
    !Number.isInteger(valuableHoldDays) ||
    valuableHoldDays < MIN_VALUABLE_HOLD_DAYS ||
    valuableHoldDays > MAX_VALUABLE_HOLD_DAYS
  ) {
    throw new Error(
      `valuableHoldDays must be an integer between ${MIN_VALUABLE_HOLD_DAYS} and ${MAX_VALUABLE_HOLD_DAYS}.`,
    )
  }
  return { holdDays, valuableHoldDays }
}

export function validateCourierAddress(input: unknown): LostFoundCourierAddress {
  if (typeof input !== 'object' || input === null) {
    throw new Error('courierAddress must be an object.')
  }
  const raw = input as Record<string, unknown>
  const linesRaw = raw.lines
  if (!Array.isArray(linesRaw) || linesRaw.length === 0 || linesRaw.length > MAX_COURIER_ADDRESS_LINES) {
    throw new Error(`courierAddress.lines must be an array of 1 to ${MAX_COURIER_ADDRESS_LINES} lines.`)
  }
  const lines: string[] = []
  for (let i = 0; i < linesRaw.length; i++) {
    const l = linesRaw[i]
    if (typeof l !== 'string' || !l.trim() || l.trim().length > MAX_COURIER_ADDRESS_LINE_LENGTH) {
      throw new Error(
        `Address line ${i + 1} must be a non-empty string <= ${MAX_COURIER_ADDRESS_LINE_LENGTH} characters.`,
      )
    }
    lines.push(l.trim())
  }

  const city = typeof raw.city === 'string' ? raw.city.trim() : ''
  if (!city || city.length > 100) {
    throw new Error('City is required and must be <= 100 characters.')
  }

  const pin = typeof raw.pin === 'string' ? raw.pin.trim() : ''
  if (!PIN_CODE_REGEX.test(pin)) {
    throw new Error('Pin code must be exactly 6 digits.')
  }

  const state = typeof raw.state === 'string' ? raw.state.trim() : ''
  if (!state || state.length > 100) {
    throw new Error('State is required and must be <= 100 characters.')
  }

  return { lines, city, pin, state }
}

export { isE164Phone, maskPhone }
